import React, { Component } from "react";
import * as Sentry from "@sentry/react";
import "./styles/error-boundary.scss";

/**
 * 全局错误边界
 *
 * 任意子组件渲染抛错时提供可恢复的降级界面，并将异常上报 Sentry。
 * React 都会把整棵组件树卸载 —— #root 被清空，页面直接白屏，
 * 而且控制台几乎无声（真正的那条报错往往在崩溃之后才抛出来）。
 *
 * 挂上这个边界后：
 * - 子组件渲染出错只降级显示错误卡片，页面骨架（Header/Footer）保留
 * - 错误照常上报 Sentry，不再需要靠 console 猜
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // 交给 Sentry，带上组件栈便于定位
        Sentry.withScope((scope) => {
            scope.setExtras({
                componentStack: info && info.componentStack,
            });
            Sentry.captureException(error);
        });
        console.error("[ErrorBoundary] 渲染出错，已降级：", error, info);
    }

    componentDidUpdate(previousProps) {
        if (
            this.state.hasError &&
            previousProps.resetKey !== this.props.resetKey
        ) {
            this.setState({ hasError: false, error: null });
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        const { hasError, error } = this.state;
        const { children } = this.props;

        if (!hasError) return children;

        return (
            <div className="mod-error-boundary">
                <span className="icon"></span>
                <p className="txt">页面出了点小问题，刷新一下试试</p>
                {import.meta.env.DEV && !!error?.message && (
                    <p className="msg">{error.message}</p>
                )}
                <div className="btns">
                    <button
                        type="button"
                        className="sub"
                        onClick={this.handleReload}
                    >
                        刷新页面
                    </button>
                    <button
                        type="button"
                        className="sub ghost"
                        onClick={this.handleReset}
                    >
                        尝试重试
                    </button>
                </div>
            </div>
        );
    }
}
